import { motion, useReducedMotion } from "framer-motion";
import { Cpu, Gift, TrendingUp, UserPlus } from "lucide-react";
import { AFG_CONTRACT, AFG_SCAN, LEVEL_REWARDS } from "../../data/site";
import { useLanguage } from "../../i18n/LanguageContext";
import { ContractPanel } from "./ContractPanel";
import { Reveal } from "./Reveal";
import { SectionHeader } from "./SectionHeader";

const BONUSES = [
    { icon: UserPlus, title: "Referral Bonus", value: "2 AFG", desc: "For every direct referral — until a total of 100,000 AFG has been distributed." },
    { icon: Gift, title: "Registration Bonus", value: "2 AFG", desc: "For registering — available to the first 50,000 registrations only." },
    { icon: Cpu, title: "Automatic Distribution", value: "100%", desc: "All token distributions are smart-contract based, automatic, transparent and secure." },
];

export const AfgToken = () => {
    const { t } = useLanguage();
    const reduce = useReducedMotion();
    return (
        <section id="afg-token" data-testid="afg-token-section" className="relative overflow-hidden border-t border-white/5 bg-nx-base py-20 sm:py-24 lg:py-32">
            <div className="pointer-events-none absolute right-0 top-1/3 h-80 w-80 rounded-full bg-nx-blue/[0.06] blur-[110px] rtl:left-0 rtl:right-auto" aria-hidden="true" />
            <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12">
                    <div className="order-2 lg:order-1 lg:col-span-5">
                        <Reveal className="relative mx-auto max-w-[400px]">
                            <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-nx-elev shadow-[0_24px_64px_rgba(0,0,0,0.55)]" data-testid="afg-token-visual">
                                <div className="pointer-events-none absolute -left-10 top-1/4 h-1/2 w-24 rounded-full bg-nx-blue/20 blur-[60px] rtl:-right-10 rtl:left-auto" aria-hidden="true" />
                                <div className="pointer-events-none absolute -bottom-10 -right-6 h-1/2 w-40 rounded-full bg-nx-lime/10 blur-[70px] rtl:-left-6 rtl:right-auto" aria-hidden="true" />
                                <motion.img
                                    src="/assets/afg-token.png"
                                    alt="AFG Token — Nexora Global"
                                    className="w-full [mask-image:radial-gradient(ellipse_82%_82%_at_50%_50%,black_62%,transparent_97%)]"
                                    animate={reduce ? undefined : { y: [0, -8, 0] }}
                                    transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                                    loading="lazy"
                                />
                                <span className="absolute left-5 top-5 rounded-full border border-white/10 bg-nx-base/60 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em] text-nx-lime backdrop-blur rtl:left-auto rtl:right-5">
                                    AFG
                                </span>
                            </div>
                            <div className="mt-6 grid grid-cols-2 gap-3">
                                <div className="rounded-2xl border border-white/5 bg-nx-elev p-5 text-center" data-testid="afg-total-supply">
                                    <span className="block text-xl sm:text-2xl font-extrabold tracking-tight text-nx-text tabular">500,000</span>
                                    <span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-nx-mute">{t("AFG — Total Supply")}</span>
                                </div>
                                <div className="rounded-2xl border border-white/5 bg-nx-elev p-5 text-center" data-testid="afg-max-supply">
                                    <span className="block text-xl sm:text-2xl font-extrabold tracking-tight text-nx-text tabular">500,000</span>
                                    <span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-nx-mute">{t("AFG — Max Supply")}</span>
                                </div>
                            </div>
                            <div className="mt-4">
                                <ContractPanel
                                    label="AFG Token Contract"
                                    address={AFG_CONTRACT}
                                    scanUrl={AFG_SCAN}
                                    scanLabel="View AFG on BscScan"
                                    testId="afg-contract"
                                />
                            </div>
                        </Reveal>
                    </div>

                    <div className="order-1 lg:order-2 lg:col-span-7">
                        <SectionHeader
                            label={t("AFG Token")}
                            testId="afg-token"
                            title={<>{t("AFG Token")}</>}
                            description={t("A blockchain-based token distribution system — 100% fair, transparent and automatic.")}
                        />
                        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
                            {BONUSES.map((b, i) => (
                                <Reveal key={b.title} delay={i * 0.08}>
                                    <div className="h-full rounded-2xl border border-white/5 bg-nx-elev p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-white/10" data-testid={`afg-bonus-${b.title.toLowerCase().replace(/\s+/g, "-")}`}>
                                        <b.icon size={18} strokeWidth={1.5} className="text-nx-lime" aria-hidden="true" />
                                        <span className="mt-4 block text-2xl font-extrabold tracking-tight text-nx-text tabular">{b.value}</span>
                                        <h3 className="mt-1 text-xs font-extrabold uppercase tracking-[0.14em] text-nx-sub">{t(b.title)}</h3>
                                        <p className="mt-2.5 text-xs leading-relaxed text-nx-mute">{t(b.desc)}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>

                        <Reveal delay={0.2} className="mt-4">
                            <div className="rounded-2xl border border-white/5 bg-nx-elev p-6" data-testid="afg-level-update-bonus">
                                <div className="flex items-center gap-3">
                                    <TrendingUp size={18} strokeWidth={1.5} className="shrink-0 text-nx-lime" aria-hidden="true" />
                                    <h3 className="text-xs font-extrabold uppercase tracking-[0.14em] text-nx-sub">{t("Level Update Bonus — Level 2 to 12")}</h3>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {LEVEL_REWARDS.map((r) => (
                                        <span
                                            key={r}
                                            className="rounded-full border border-nx-blue/30 bg-nx-blue/[0.07] px-3 py-1.5 text-xs font-extrabold text-nx-text tabular"
                                        >
                                            {r.toLocaleString("en-US")} <span className="text-nx-lime">AFG</span>
                                        </span>
                                    ))}
                                </div>
                                <p className="mt-4 text-xs leading-relaxed text-nx-mute">
                                    {t("Rewards are paid to both you and your direct referrer, until a total of 200,000 AFG has been distributed.")}
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
};
